import axios, {AxiosError, AxiosRequestConfig, AxiosResponse} from 'axios';
import i18next from 'i18next';

import {fetchAccessToken} from 'api';
import {cleanAuthData} from 'models/auth';
import {TokensData} from 'types';

import {createRestError} from './errors';
import {TokenStorage} from './tokenStorage';

export const UNAUTHORIZED_ROUTES = ['/uaa/login', '/uaa/refresh-token'];

export const AUTH_TOKEN_NAME = 'Authorization';
let REFRESH_TOKEN_PROMISE: null | Promise<TokensData> = null;

export const apiClient = axios.create({baseURL: '/api/v1'});

async function updateTokens(): Promise<TokensData | null> {
    const {refreshToken, refreshTokenExpiresIn} = TokenStorage;
    if (!refreshToken) {
        REFRESH_TOKEN_PROMISE = null;
        return null;
    }

    try {
        if (!REFRESH_TOKEN_PROMISE) {
            TokenStorage.accessToken = null;
            REFRESH_TOKEN_PROMISE = fetchAccessToken(refreshToken);
        }

        const tokensData = await REFRESH_TOKEN_PROMISE;
        TokenStorage.storeTokens(tokensData);
        REFRESH_TOKEN_PROMISE = null;
        return tokensData;
    } catch (refreshTokenError) {
        REFRESH_TOKEN_PROMISE = null;
        return null;
    }
}

async function onErrorInterceptor(e: AxiosError) {
    if (e.response?.status !== 401) throw createRestError(e);

    const tokensData = await updateTokens();
    if (!tokensData) {
        cleanAuthData();
        throw new Error(i18next.t('errors.auth.invalidRefreshToken'));
    }

    e.config.headers[AUTH_TOKEN_NAME] = `Bearer ${tokensData.accessToken}`;
    return apiClient.request(e.config);
}

async function onRequestInterceptor(clientConfig: AxiosRequestConfig) {
    const {url} = clientConfig;

    if (url != null && UNAUTHORIZED_ROUTES.includes(url)) {
        clientConfig.headers[AUTH_TOKEN_NAME] = undefined;
        return clientConfig;
    }

    const {accessToken, accessTokenExpiresIn} = TokenStorage;

    if (
        accessToken == null ||
        accessTokenExpiresIn == null ||
        new Date() >= new Date(accessTokenExpiresIn)
    ) {
        const tokensData = await updateTokens();
        if (tokensData != null) {
            clientConfig.headers[AUTH_TOKEN_NAME] = `Bearer ${tokensData.accessToken}`;
            return clientConfig;
        }
    }

    clientConfig.headers[AUTH_TOKEN_NAME] = accessToken ? `Bearer ${accessToken}` : undefined;
    return clientConfig;
}

apiClient.interceptors.response.use((response) => response, onErrorInterceptor);
apiClient.interceptors.request.use(onRequestInterceptor);
